import React from "react";
import { ConsentType, consentManagement } from "shared/consentManagement/consent";
import { useGetBannerCopy, useRecordConstent } from "./dataloader";
import { ConsentAcceptanceType } from "shared/enums/ConsentAcceptanceType";
import { ApiGDPRConsentResponse } from "shared/types/ApiGDPRConsentResponse";
import { useGoogleTagManager } from "shared/consentManagement/scripts";
import { isInIframe } from "shared/utils/iframe";
import { useSearchParams } from "react-router-dom";
import QueryParamKey from "shared/enums/QueryParamKey";
import { CookieConsentType } from "shared/consentManagement/enum";

const Context = React.createContext<ConsentProviderType | undefined>(undefined);

export type ConsentProviderType = {
  analyticalConsentProvided: boolean;
  grantFullConsent: (consentAcceptanceType: ConsentAcceptanceType) => void;
  grantConsent: (consentAcceptanceType: ConsentAcceptanceType, consentType: ConsentType) => void;
  revokeFullConsent: () => void;
  consentCopy: ApiGDPRConsentResponse | undefined;
  canShowCookieConsentButton: boolean;
  hasAnyConsent: boolean;
  updateConsent: (consentAcceptanceType: ConsentAcceptanceType, consentType: ConsentType) => void;
  hasStoredCookieConsent: boolean;
};

function ConsentProvider({ children }: { children: React.ReactNode }) {
  const { mutateAsync: logConsent } = useRecordConstent();
  const { data } = useGetBannerCopy();
  const [key, setKey] = React.useState(1);
  const [searchParams] = useSearchParams();
  useGoogleTagManager(data?.gtmKey);

  const _baseConsentCheck = React.useCallback(
    (consent: boolean) => {
      if (!data) {
        return false;
      }
      const isComplianceRequired = Boolean(data?.gdprConsentEnabled);
      if (isComplianceRequired) {
        return consent;
      }
      return !!key;
    },
    [data, key]
  );

  const analyticalConsentProvided = React.useMemo(() => {
    if (isInIframe) {
      if (!searchParams.get(QueryParamKey.COOKIES)) {
        return true;
      } else {
        return searchParams.get(QueryParamKey.COOKIES) === CookieConsentType.ANALYTICAL;
      }
    }
    return _baseConsentCheck(consentManagement.hasAnalyticalConsent());
  }, [_baseConsentCheck, searchParams]);

  const hasAnyConsent = React.useMemo(() => {
    return analyticalConsentProvided;
  }, [analyticalConsentProvided]);

  const hasStoredCookieConsent = React.useMemo(() => {
    if (!data) {
      return false;
    }
    if (data.gdprConsentEnabled) {
      return !!key && consentManagement.hasConsentCookie();
    }
    return true;
  }, [key, data]);

  const canShowCookieConsentButton = React.useMemo(() => {
    const isComplianceRequired = Boolean(data?.gdprConsentEnabled);
    return isComplianceRequired;
  }, [data?.gdprConsentEnabled]);

  const grantFullConsent = React.useCallback(
    async (consentAcceptanceType: ConsentAcceptanceType) => {
      consentManagement.grantFullConsent();
      const updatedConsent = consentManagement.getConsentStatus();
      await logConsent({
        consent: {
          consentType: consentAcceptanceType,
          necessaryCookieConsent: updatedConsent.necessary,
          functionalCookieConsent: updatedConsent.functional,
          analyticalCookieConsent: updatedConsent.analytical,
          targetingCookieConsent: updatedConsent.targeting,
          consentVersion: updatedConsent.version
        }
      });
      setKey(key + 1);
    },
    [logConsent, key]
  );

  const grantConsent = React.useCallback(
    async (consentAcceptanceType: ConsentAcceptanceType, consentType: ConsentType) => {
      consentManagement.grantConsent(consentType);
      const updatedConsent = consentManagement.getConsentStatus();
      setKey(key => key + 1);
      await logConsent({
        consent: {
          consentType: consentAcceptanceType,
          necessaryCookieConsent: updatedConsent.necessary,
          functionalCookieConsent: updatedConsent.functional,
          analyticalCookieConsent: updatedConsent.analytical,
          targetingCookieConsent: updatedConsent.targeting,
          consentVersion: updatedConsent.version
        }
      });
    },
    [logConsent]
  );
  const updateConsent = React.useCallback((consentAcceptanceType: ConsentAcceptanceType, consentType: ConsentType) => {
    consentManagement.grantConsent(consentType);
    setKey(key => key + 1);
  }, []);

  const revokeFullConsent = React.useCallback(() => {
    consentManagement.revokeFullConsent();
    setKey(key => key + 1);
  }, []);

  const values = React.useMemo(() => {
    return {
      analyticalConsentProvided,
      grantFullConsent,
      revokeFullConsent,
      consentCopy: data,
      canShowCookieConsentButton,
      hasAnyConsent,
      grantConsent,
      key,
      updateConsent,
      hasStoredCookieConsent
    };
  }, [
    analyticalConsentProvided,
    grantFullConsent,
    revokeFullConsent,
    data,
    canShowCookieConsentButton,
    hasAnyConsent,
    grantConsent,
    key,
    updateConsent,
    hasStoredCookieConsent
  ]);

  return <Context.Provider value={values}>{children}</Context.Provider>;
}

function useConsent(): ConsentProviderType {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error(`useConsent must be used within a ConsentProvider`);
  }
  return context;
}

export { ConsentProvider, useConsent };
